import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaEdit, FaSave, FaTimes, FaUndo, FaTruck } from "react-icons/fa";
import { toast } from "react-toastify";
import VehicleFormData from "./VehicleFormData";

const VehicleForm = () => {
  const { control, handleSubmit, setValue, reset, register, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [isEditable, setIsEditable] = useState(false);

  const fetchVehicle = async (vehicleNumber) => {
    const clientId = process.env.REACT_APP_CLIENT_ID; // Replace with actual clientId
    const secretKey = process.env.REACT_APP_SECRET_ID; // Replace with actual secretKey

    try {
        const response = await fetch(
            "https://api.invincibleocean.com/invincible/vehicleRegistrationV10",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    clientId: clientId,
                    secretKey: secretKey,
                },
                body: JSON.stringify({ vehicleNumber }),
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Vehicle Data:", data);
        return data;
    } catch (error) {
        console.error("Error fetching vehicle data:", error);
        return null;
    }
};


  const fetchVehicleData = async (vehicleNumber) => {
    setLoading(true);
    try {

      const data = await fetchVehicle(vehicleNumber)
      console.log(data)

      // const clientID = 'f010ec6b710db2e8500200e3c85bcc77:4bca9e19f8719a02036c408acdd6b61a'
      // const secretKey = 'BFnxyy7KAusKWgRgBrFXkzKzXn9tLNgjb9KTqyuPcwYqmbO8TvXqU9GxczjCINtmM'
      // const dataRes = await fetch(
      //   `${process.env.REACT_APP_VEHICLE_API}${vehicleNumber}&consent=y`
      // );

      // const response = await dataRes.json()

      if (data.code===200) {
        setVehicleData(data.result);
        Object.keys(data.result).forEach((key) => {
          if (data.result[key] && typeof data.result[key] !== "object")
            setValue(key, data.result[key]);
        });
        toast.success("Vehicle data fetched successfully!");
      } else {
        toast.error("No vehicle found with this number!");
      }
    } catch (error) {
      console.error("Error fetching vehicle data:", error);
      toast.error("Failed to fetch vehicle data.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      await axios.put(`/api/trucks/update/${vehicleData._id}`, data);
      toast.success("Vehicle updated successfully!");
      setIsEditable(false);
    } catch (error) {
      console.error("Error updating vehicle data:", error);
      toast.error("Failed to update vehicle.");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
      className="max-w-4xl mx-auto p-4 sm:p-6"
    >
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 sm:p-6 text-white">
          <div className="flex items-center space-x-3">
            <FaTruck className="text-2xl sm:text-3xl" />
            <h2 className="text-xl sm:text-2xl font-bold">Vehicle Management</h2>
          </div>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base opacity-90">Search and manage vehicle information</p>
        </div>

        <div className="p-4 sm:p-6">
          <AnimatePresence mode="wait">
            {!vehicleData ? (
              <motion.form
                key="search-form"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onSubmit={handleSubmit(({ vehicleNumber }) =>
                  fetchVehicleData(vehicleNumber)
                )}
                className="space-y-4"
              >
                <div>
                  <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                    Vehicle Number
                  </label>
                  <div className="relative">
                    <input
                      {...register("vehicleNumber", { 
                        required: "Vehicle number is required",
                        pattern: {
                          value: /^[A-Z0-9\s-]+$/i,
                          message: "Please enter a valid vehicle number"
                        }
                      })}
                      type="text"
                      placeholder="Enter vehicle number (e.g., MH-01-AB-1234)"
                      className="w-full pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base sm:text-lg" />
                  </div>
                  {errors.vehicleNumber && (
                    <p className="mt-1 text-sm text-red-500">{errors.vehicleNumber.message}</p>
                  )}
                </div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-70"
                >
                  {loading ? (
                    <span>Searching...</span>
                  ) : (
                    <>
                      <FaSearch className="text-base sm:text-lg" />
                      <span>Search Vehicle</span>
                    </>
                  )}
                </motion.button>
              </motion.form>
            ) : (
              <motion.div
                key="vehicle-form"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {Object.entries(vehicleData).map(([key, value], index) => (
                      <motion.div
                        key={key}
                        variants={containerVariants}
                        transition={{ delay: index * 0.1 }}
                      >
                        <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">
                          {key.replace(/_/g, " ").toUpperCase()}
                        </label>
                        <Controller
                          name={key}
                          control={control}
                          defaultValue={value || ""}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="text"
                              className={`w-full px-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg border ${
                                isEditable
                                  ? "border-blue-300 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  : "border-gray-200 bg-gray-50"
                              } transition-all duration-200`}
                              disabled={!isEditable}
                            />
                          )}
                        />
                      </motion.div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center justify-end space-x-4 pt-4 sm:pt-6 border-t">
                    <motion.button
                      type="button"
                      onClick={() => {
                        setVehicleData(null);
                        reset();
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center px-3 py-2 sm:px-4 sm:py-2 text-sm sm:text-base text-white bg-blue-500 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                    >
                      <FaUndo className="mr-2" />
                      New Search
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
          <VehicleFormData vehicleData={vehicleData} />
        </div>
      </div>
    </motion.div>
  );
};

export default VehicleForm;
